import CandidateService, {
    WorkAuthorization,
    Candidate,
    Language,
} from '~/services/CandidateService';
import CommunicationService, { EmailSettings } from '~/services/CommunicationService';
import CSPService, { CandidateStudyProgram } from '~/services/CSPService';
import IdentityService, { User } from '~/services/IdentityService';
import { VFairProfileRequirementLevel } from '~/services/VFairService';
import { urlValidator } from '../urlUtils';
import { emailValidator, genderValidator } from '../validators';

export interface IMissingFieldValidation {
    firstName: boolean;
    lastName: boolean;
    pronoun: boolean;
    disability: boolean;
    nationality: boolean;
    ethnicMinority: boolean;
    country: boolean;
    gender: boolean;
    email: boolean;
    languages: boolean;
    candidateStudyPrograms: boolean;
    terms: boolean;
}

export interface IProfileData {
    candidateData: Candidate;
    identityData: User;
    emailSettings: EmailSettings;
    workAuthorization: WorkAuthorization[];
    terms: boolean;
    candidateStudyPrograms: CandidateStudyProgram[];
}

interface IErrorMessage {
    error: boolean;
    message: string | null;
}

const PRONOUN_TYPE_OTHER = 'other';

export function getRequiredFields(level?: VFairProfileRequirementLevel): IMissingFieldValidation {
    const none: IMissingFieldValidation = {
        email: false,
        firstName: false,
        lastName: false,
        pronoun: false,
        nationality: false,
        country: false,
        gender: false,
        disability: false,
        ethnicMinority: false,
        languages: false,
        candidateStudyPrograms: false,
        terms: false,
    };

    switch (level) {
        case VFairProfileRequirementLevel.Minimum:
            return {
                ...none,

                firstName: true,
                lastName: true,
                terms: true,
            };
        default:
            return {
                ...none,

                email: true,
                firstName: true,
                lastName: true,
                nationality: true,
                country: true,
                gender: true,
                languages: true,
                candidateStudyPrograms: true,
                terms: true,
            };
    }
}

// Checks nested object for {error: true}
function hasError(obj: { error?: boolean; [key: string]: any }): boolean {
    if (!obj || (typeof obj !== 'object' && !Array.isArray(obj))) {
        return false;
    } else if (!!obj.error) {
        return true;
    } else if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) if (hasError(obj[i])) return true;
    } else {
        for (const k in obj) if (hasError(obj[k])) return true;
    }
    return false;
}

export function validateProfileData(
    userWrapper: IProfileData,
    requiredFields: IMissingFieldValidation
): IMissingFieldValidation {
    return {
        firstName: !!userWrapper.identityData.firstName || !requiredFields.firstName,
        lastName: !!userWrapper.identityData.lastName || !requiredFields.lastName,
        email: emailValidator(userWrapper.identityData.email || '') || !requiredFields.email,

        country: !!userWrapper.candidateData.country || !requiredFields.country,
        nationality: !!userWrapper.identityData.nationality || !requiredFields.nationality,
        gender: genderValidator(userWrapper.identityData.gender || '') || !requiredFields.gender,
        pronoun: pronounValidator(userWrapper) || !requiredFields.pronoun,
        ethnicMinority:
            !!userWrapper.candidateData.ethnicMinority || !requiredFields.ethnicMinority,
        disability: !!userWrapper.candidateData.disability || !requiredFields.disability,

        languages:
            (!!userWrapper.candidateData.languages &&
                !!userWrapper.candidateData.languages.length &&
                !!userWrapper.candidateData.languages.every(
                    (l: Language) => !!l.languageId && !!l.proficiencyId
                )) ||
            !requiredFields.languages,
        candidateStudyPrograms:
            (!!userWrapper.candidateStudyPrograms && !!userWrapper.candidateStudyPrograms.length) ||
            !requiredFields.candidateStudyPrograms,

        terms: userWrapper.terms || !requiredFields.terms,
    };
}

function pronounValidator(userWrapper: any): boolean {
    if (
        !!userWrapper.candidateData.pronoun &&
        userWrapper.candidateData.pronoun.type == PRONOUN_TYPE_OTHER
    )
        return !!userWrapper.candidateData.pronoun.value;

    return !!userWrapper.candidateData.pronoun;
}

export async function loadUserProfileData(): Promise<IProfileData> {
    const identityService = new IdentityService();
    const candidateService = new CandidateService();
    const cspService = new CSPService();

    const userWrapper = {
        identityData: {} as User,
        candidateData: {} as Candidate,
        workAuthorization: [] as WorkAuthorization[],
        emailSettings: {} as EmailSettings,
        terms: false,
        candidateStudyPrograms: [] as CandidateStudyProgram[],
    } as IProfileData;

    const promises = [
        CommunicationService.getEmailSettings().then((settings: EmailSettings) => {
            userWrapper.emailSettings = settings;
        }),
        identityService.getMe().then((user: User) => {
            user.image = user.image || user.avatar;
            userWrapper.identityData = user;
            userWrapper.terms = !!user.termsConsent;
        }),
        candidateService.getMe().then((candidate: Candidate) => {
            userWrapper.candidateData = candidate;
        }),
        candidateService.getWorkAuthorizations().then((wa: WorkAuthorization[]) => {
            userWrapper.workAuthorization = wa;
        }),
        // TODO: move to /me endpoint instead
        cspService.getCandidateStudyPrograms().then((csps: CandidateStudyProgram[]) => {
            userWrapper.candidateStudyPrograms = csps;
        }),
    ];

    return Promise.all(promises).then(() => userWrapper);
}

export default (userData: User, candidateData: Candidate) => {
    const errors: {
        isError: boolean;
        userData: Object;
        candidateData: {
            links: {
                name: IErrorMessage;
                value: IErrorMessage;
            }[];
        };
    } = {
        isError: false,
        userData: {},
        candidateData: { links: [] },
    };

    // TODO Links validation

    errors.isError = hasError(errors);
    return errors;
};
